import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormGroup, AbstractControl, FormBuilder, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material';
import { ConvertService } from 'src/app/services/convert.service';
import { AuthStore } from 'src/app/stores/auth/auth.store';
import { ResourceStore } from 'src/app/stores/resources/resource.store';
import { DataService } from 'src/app/services/data.service';
import { FireValidatorsService } from 'src/app/services/fire-validators.service';
import { ILesson } from 'src/app/interfaces/resource';
import { STATUS_OBJECT, DISPLAY_STATUS_OBJECT } from 'src/app/dummy/status';
import { COLLECTION_NAME } from 'src/app/dummy/app';
import { Observable } from 'rxjs';
import { FireStorageService } from 'src/app/services/fire-storage.service';
import { toJS } from 'mobx';
import { orderBy } from 'src/app/services/mapping.service';

@Component({
  selector: 'app-lesson-form',
  templateUrl: './lesson-form.component.html',
  styleUrls: ['./lesson-form.component.scss']
})
export class LessonFormComponent implements OnInit {
  @ViewChild('focusInput', {static:false}) inputEl: ElementRef;
  @ViewChild('coverFile', {static:false}) coverFile: ElementRef;

  ITEM_KEY: any;
  ITEM: ILesson;
  TITLE: any = "Add new lesson"
  LEVEL_LIST: any;
  PROGRAM_LIST: any;
  START_UPLOAD: boolean = false;
  uploadPercentCover: Observable<number>;
  SELECTED_COVER_FILE: any;

  form: FormGroup;
  name: AbstractControl;
  description: AbstractControl;
  processNew: boolean = false;
  processClose: boolean = false;
  error: any = null;
  isLinear = false;
  LOADING = true;
  selectedFiles: any;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private auth: AuthStore,
    public store: ResourceStore,
    public ds: DataService,
    private snackBar: MatSnackBar,
    private storage: FireStorageService
  ) { }


  async uploadFileCover(event) {
    this.SELECTED_COVER_FILE = event;
    if (this.ITEM) {
      this.START_UPLOAD = true;
      const fileName = event.target.files[0].name;
      const coverPath = `lessons/${this.ITEM.key}/cover/${fileName}`;
      const coverTask = this.storage.uploadFile(this.SELECTED_COVER_FILE, coverPath);
      this.uploadPercentCover = coverTask.percentageChanges();
      const downloadURLCover = (await coverTask).ref.getDownloadURL();
      downloadURLCover.then(downloadURL => {
        this.store.updateCoverDownloadURL(this.ITEM, downloadURL, fileName, (success, res) => {
          this.snackBar.open("Cover has been uploaded successful.", "Done", { duration: 1000 });
          this.START_UPLOAD = false;
          this.coverFile.nativeElement.value = "";
        })
      })
    }
  }


  async ngOnInit() {
    this.form = this.fb.group({
      name: [null, Validators.required, FireValidatorsService.checkExistByCompany(this.ds.firestoreRef(), this.auth.selectedCompanyKey, COLLECTION_NAME.LESSON, "name")],
      description: [null],
      program: [null],
      level: [null],
    })
    this.LEVEL_LIST = orderBy(await this.store.fetchListArray(COLLECTION_NAME.LEVEL, "name"), 'order');

    this.PROGRAM_LIST = await this.store.fetchListArray(COLLECTION_NAME.PROGRAM, "name")
    this.route.params.subscribe(param => {
      const { id } = param;
      if (id !== 'draft') {
        this.ITEM_KEY = param.id;
        this.store.fetchSelectedData(COLLECTION_NAME.LESSON, this.ITEM_KEY, (doc: ILesson) => {
          if (doc) {
            this.TITLE = "Edit lesson"
            this.ITEM = doc;
            this.form = this.fb.group({
              name: [null, Validators.required, FireValidatorsService.checkExistByCompany(this.ds.firestoreRef(), this.auth.selectedCompanyKey, COLLECTION_NAME.LESSON, "name", this.ITEM.name)],
              description: [null],
              program: [null],
              level: [null],
            })
            const { name, description, programKeys, levelKeys } = doc;
            this.form.patchValue({
              name, description, program: programKeys, level: levelKeys
            })
            this.inputEl.nativeElement.focus();
            this.LOADING = false;
            return;
          }
          else {
            this.ITEM = null;
            this.router.navigate([`/lessons`])
          }
        })
      }
      else {
        this.ITEM = null;
        this.ITEM_KEY = null;
        this.LOADING = false;
      }
    })
  }

  async onSave(f: any, isDraft: boolean) {
    if (!this.SELECTED_COVER_FILE && !this.ITEM) {
      alert('Please select cover file');
      return;
    }
    if (this.form.valid) {
      this.processClose = isDraft ? false : true;
      this.processNew = isDraft;
      this.error = null;
      this.form.disable();
      const { name, description, program, level } = f;
      const { user, selectedCompanyKey } = this.auth;
      const registerStatus = isDraft ? STATUS_OBJECT.draft : STATUS_OBJECT.submitted;
      const PROGRAM_KEY = program ? program.map(m => m) : [];
      const LEVEL_KEY = level ? level.map(m => m) : []
      const key = this.ITEM ? this.ITEM.key : this.ds.createId();
      const item: ILesson = {
        key: key,
        status: DISPLAY_STATUS_OBJECT.ACTIVE,
        isDelete: false,
        created_at: this.ITEM ? this.ITEM.created_at : new Date(),
        created_by: this.ITEM ? this.ITEM.created_by : user.uid,
        updated_at: new Date(),
        updated_by: user.uid,
        date_key: this.ITEM ? this.ITEM.date_key : ConvertService.dateKey(),
        page_key: ConvertService.pageKey(),
        keyword: ConvertService.toCapitalize(`${name.trim()}`),
        organizationRef: this.ds.companyRef().doc(selectedCompanyKey).ref,
        organizationKey: selectedCompanyKey,
        created_byRef: this.ITEM ? this.ITEM.created_byRef : this.ds.userProfileRef().doc(user.uid).ref,
        updated_byRef: this.ds.userProfileRef().doc(user.uid).ref,
        register_status: registerStatus,
        //fields
        name: name.trim(),
        description,
        programKeys: PROGRAM_KEY,
        programRef: PROGRAM_KEY ? PROGRAM_KEY.map(m => (this.ds.firestoreRef().collection(COLLECTION_NAME.PROGRAM).doc(m).ref)) : [],
        levelKeys: LEVEL_KEY,
        levelRef: LEVEL_KEY ? LEVEL_KEY.map(m => (this.ds.firestoreRef().collection(COLLECTION_NAME.LEVEL).doc(m).ref)) : [],
        fileurl: this.ITEM ? this.ITEM.fileurl : null,
        filename: this.ITEM ? this.ITEM.filename : null,
      }
      if (!this.ITEM) {
        this.START_UPLOAD = true;
        const fileName = this.SELECTED_COVER_FILE.target.files[0].name;
        const coverPath = `lessons/${key}/cover/${fileName}`;
        const coverTask = this.storage.uploadFile(this.SELECTED_COVER_FILE, coverPath);
        this.uploadPercentCover = coverTask.percentageChanges();
        const downloadURLCover = (await coverTask).ref.getDownloadURL();
        downloadURLCover.then(coverUrl => {
          item.fileurl = coverUrl;
          item.filename = fileName;
          this.store.addItem(COLLECTION_NAME.LESSON, item, (success, res) => {
            if (success) {
              this.snackBar.open("Lesson has been saved successful.", "Done", { duration: 2500 });
              this.form.reset();
              this.SELECTED_COVER_FILE = null;
            }
            this.form.enable();
            this.processNew = false;
            this.processClose = false;
            this.START_UPLOAD = false;
            this.inputEl.nativeElement.focus();
            this.coverFile.nativeElement.value = "";
            if (this.ITEM_KEY) {
              this.router.navigate([`/lessons/submitted`])
            }
            else {
              this.router.navigate([`/lessons/lesson-form/draft`])
            }
          })
        })

      }
      else {
        this.store.addItem(COLLECTION_NAME.LESSON, item, (success, res) => {
          if (success) {
            this.snackBar.open("Lesson has been saved successful.", "Done", { duration: 2500 });
            this.form.reset();
          }
          this.form.enable();
          this.processNew = false;
          this.processClose = false;
          this.inputEl.nativeElement.focus();
          if (this.ITEM_KEY) {
            const status = 'submitted'//isDraft ? 'draft' : 'submitted'
            this.router.navigate([`/lessons/${status}`])
          }
        })
      }
    }
    else {
      this.inputEl.nativeElement.focus();
      this.processNew = false;
      this.processClose = false;
    }
  }

  goBack() {
    const status = 'submitted' //!this.ITEM_KEY ? 'draft' : 'submitted'
    this.router.navigate([`/lessons/${status}`])
  }

}
